import { I18N_CONFIG } from '@indutrust/shared-model';
import type { InlineLoader, Translation } from '@jsverse/transloco';

/**
 * Builds a Transloco scope loader whose keys are the language codes declared
 * in `I18N_CONFIG.languages`, so adding a new language only requires updating
 * the central config and shipping the matching `<code>.json` files.
 *
 * The `importer` callback must perform a relative dynamic import of the form
 * `` import(`./${lang}.json`) ``. The relative `./` must live at the call site
 * (not inside this helper) so the bundler can statically discover the sibling
 * JSON files of the calling module and emit one chunk per language.
 *
 * @example
 * ```ts
 * import { createI18nLoader } from '@indutrust/shared-util';
 *
 * export const i18nLoader = createI18nLoader(
 *   (lang) => import(`./${lang}.json`),
 * );
 * ```
 */
export function createI18nLoader(
  importer: (lang: string) => Promise<unknown>
): InlineLoader {
  return Object.fromEntries(
    I18N_CONFIG.languages.map((language) => [
      language.code,
      async (): Promise<Translation> => {
        const translations = await importer(language.code);
        // JSON modules resolve to `any` at the dynamic-import boundary.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Transloco Translation shape from JSON scope files
        return translations as Translation;
      },
    ])
  );
}
